import React, { useMemo, useCallback, useRef } from 'react';
import { withParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import ElementTrends from './ElementTrends';
import NoActivity from './NoActivity';
import styles from './CapabilityTrendsChart.module.scss';

function ElementTrendSetInternal({
  parentWidth,
  trendHeight = 200,
  trends,
  paletteMap,
  metrics,
  onHoverTrend,
  onUnhoverTrend,
  onClickTrend,
  filter,
}) {
  const margin = { top: 10, right: 15, bottom: 15, left: 15 };

  const { maxDate, minDate } = useMemo(() => {
    const minTimeRangeInMs = 30 * 24 * 60 * 60 * 1000;
    const postNowBufferInMs = 1 * 24 * 60 * 60 * 1000;
    if (!metrics) {
      return {
        maxDate: new Date(),
        minDate: new Date(new Date().getTime() - minTimeRangeInMs),
      };
    }

    let { maxDate, minDate } = Object.values(metrics).reduce(
      (acc, { userGroupAssessment }) => {
        return {
          maxDate:
            acc.maxDate && acc.maxDate > userGroupAssessment.went_live_at
              ? acc.maxDate
              : userGroupAssessment.went_live_at,
          minDate:
            acc.minDate && acc.minDate < userGroupAssessment.went_live_at
              ? acc.minDate
              : userGroupAssessment.went_live_at,
        };
      },
      { maxDate: null, minDate: null }
    );

    if (!maxDate) {
      maxDate = new Date();
    }

    if (
      !minDate ||
      maxDate.getTime() - minDate.getTime() <
        minTimeRangeInMs - postNowBufferInMs
    ) {
      const maxDateTime = maxDate.getTime();
      minDate = new Date(maxDateTime - (minTimeRangeInMs - postNowBufferInMs));
      maxDate = new Date(maxDateTime + postNowBufferInMs);
    }

    const availableWidth = parentWidth - margin.left - margin.right;
    const pixelsPerMs =
      availableWidth / (maxDate.getTime() - minDate.getTime());
    const bufferMs = 10 / pixelsPerMs;

    const bufferedMinDate = new Date(minDate.getTime() - bufferMs);
    const bufferedMaxDate = new Date(maxDate.getTime() + bufferMs);

    return { maxDate: bufferedMaxDate, minDate: bufferedMinDate };
  }, [metrics, parentWidth, margin.left, margin.right]);

  const scaleX = useMemo(
    () =>
      scaleTime({
        domain: [minDate, maxDate],
        range: [0, parentWidth - margin.left - margin.right],
      }),
    [parentWidth, minDate, maxDate, margin.left, margin.right]
  );

  const scaleY = useMemo(
    () =>
      scaleLinear({
        domain: [0, 1],
        range: [trendHeight - margin.bottom - margin.top, 0],
      }),
    [trendHeight, margin.bottom, margin.top]
  );

  const resetTrendTimeout = useRef(null);

  const handleTrendOver = useCallback(
    (capabilityId) => {
      clearTimeout(resetTrendTimeout.current);
      resetTrendTimeout.current = null;
      onHoverTrend(capabilityId);
    },
    [onHoverTrend, resetTrendTimeout]
  );

  const handleTrendOut = useCallback(() => {
    clearTimeout(resetTrendTimeout.current);
    resetTrendTimeout.current = setTimeout(() => onUnhoverTrend(), 50);
  }, [onUnhoverTrend, resetTrendTimeout]);

  return (
    <div className={styles['element-trends-set']}>
      {trends?.length === 0 && (
        <NoActivity
          width={parentWidth}
          height={trendHeight}
          margin={margin}
          scaleX={scaleX}
          scaleY={scaleY}
        />
      )}
      {trends &&
        trends.map((t) => (
          <div key={t.element.guid} className={styles['element-group']}>
            <label className={styles['element-trend-label']}>
              {t.element.name}
            </label>
            <div>
              <ElementTrends
                data={t.capabilityTrends}
                width={parentWidth}
                height={trendHeight}
                paletteMap={paletteMap}
                margin={margin}
                scaleX={scaleX}
                scaleY={scaleY}
                onHoverTrend={handleTrendOver}
                onUnhoverTrend={handleTrendOut}
                onClickTrend={onClickTrend}
                filter={filter}
              />
            </div>
          </div>
        ))}
    </div>
  );
}

export const ElementTrendSet = withParentSize(ElementTrendSetInternal);

export default ElementTrendSet;
